import { Link } from 'react-router-dom';
import type { VideoItem } from '@/mocks/videos';

interface VideoCardProps {
  video: VideoItem;
  variant?: 'default' | 'compact' | 'featured';
}

export default function VideoCard({ video, variant = 'default' }: VideoCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${video.id}`}
        className="group block"
      >
        <div className="relative overflow-hidden rounded-lg aspect-video mb-3">
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <span className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-0.5 rounded-md whitespace-nowrap">
            {video.duration}
          </span>
        </div>
        <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
          {video.title}
        </h3>
        <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatCount(video.viewCount)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-500"></i>
            {video.rating}
          </span>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${video.id}`}
        className="group block relative overflow-hidden rounded-lg"
      >
        <div className="aspect-[16/7] relative">
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium mb-3 whitespace-nowrap">
              {video.categoryName}
            </span>
            <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-2 font-heading">
              {video.title}
            </h3>
            <p className="text-background-200/80 text-sm line-clamp-2 max-w-xl">
              {video.description}
            </p>
            <div className="flex items-center gap-4 mt-3 text-sm text-background-300">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatCount(video.viewCount)} 次播放
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                {video.rating}
              </span>
              <span>{video.duration}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${video.id}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300"
    >
      <div className="relative overflow-hidden aspect-video">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <span className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-0.5 rounded-md whitespace-nowrap">
          {video.duration}
        </span>
        {video.hot && (
          <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
            热门
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug mb-2">
          {video.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-1 mb-3">{video.description}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
            {video.categoryName}
          </span>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatCount(video.viewCount)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-500"></i>
              {video.rating}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function formatCount(count: number): string {
  if (count >= 10000) {
    return (count / 10000).toFixed(1) + '万';
  }
  return count.toString();
}